<script>
  import { browser } from '$app/environment'
  import { innerWidth } from 'svelte/reactivity/window'
  import { onDestroy, onMount } from 'svelte'

  let {
    data,
    index = 0,
    offset = 0,
    progress = 0,
    enableTypingEffect = true,
    enableLastCardTypingEffect = true,
    enableBorderAnimation = true,
    requiredWords = [],
    rowSpacingDesktop = 45,
    rowSpacingMobile = 30,
    activeDescFontSize = 18,
    activeDescLineHeight = 1.5,
    activeDescGap = 25,
    activeBoxBgColor = 'rgba(63, 85, 165, 0.35)',
    animSpeed = 0.015,
    emptyCardFontSizeDesktop = 30,
    emptyCardFontSizeMobile = 22,
    emptyCardLineHeight = 1.5,
    emptyCardBgOpacity = 0.25
  } = $props()

  let canvas
  let ctx
  let isReady = $state(false)
  let w = $state(0)
  let h = $state(0)
  let dpr = $state(1)
  let frameId
  let introStartTime = 0

  // Grid Configuration
  // Increase these values to add more margin between rows on the BG

  // Active Box Description Configuration
  let activeDescFontFamily = $state('sans-serif')

  // Empty Card (4th Screen) Configuration
  const emptyCardFontSize = $derived(innerWidth.current > 744 ? emptyCardFontSizeDesktop : emptyCardFontSizeMobile)
  let emptyCardFontFamily = $state('"CuratorLight", sans-serif')
  const emptyCardMaxWidth = $derived(
    innerWidth.current > 744 ? 600 : Math.max(280, innerWidth.current - 40),
  )

  // --- 1. DATA PREPARATION ---
  let cachedData = null
  let displayData = $derived.by(() => {
    if (cachedData) return cachedData
    if (!Array.isArray(data) || data.length === 0) return { items: [], req3: null }

    let seed = 12345
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280
      return seed / 233280
    }

    const map = new Set()
    const filteredData = data.filter((item) => item['area-name'] && item['bto-name'])

    const requiredItems = []
    const remainingItems = []
    const addedRequiredWords = new Set()

    filteredData.forEach((item) => {
      const btoName = String(item['bto-name'] || '').toLowerCase()
      const matchedEntry = requiredWords.find((w) => btoName.includes(w.word.toLowerCase()))
      if (matchedEntry && !addedRequiredWords.has(matchedEntry.word.toLowerCase())) {
        addedRequiredWords.add(matchedEntry.word.toLowerCase())
        const areaName = String(item['area-name'] || '')
        map.add(areaName)
        requiredItems.push({
          ...item,
          isRequired: true,
          reqIndex: requiredWords.indexOf(matchedEntry),
          reqDescription: matchedEntry.description,
        })
      }
    })

    filteredData.forEach((item) => {
      const areaName = String(item['area-name'] || '')
      if (!map.has(areaName)) {
        map.add(areaName)
        remainingItems.push({ ...item, isRequired: false })
      }
    })

    for (let i = remainingItems.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1))
      ;[remainingItems[i], remainingItems[j]] = [remainingItems[j], remainingItems[i]]
    }

    const itemsPerRow = 15
    const targetItemCount = 50 * itemsPerRow - requiredItems.length
    const allItems = []
    while (allItems.length < targetItemCount) {
      allItems.push(...remainingItems)
    }
    allItems.length = targetItemCount

    const req3 = requiredItems.find((r) => r.reqIndex === 3)
    const otherReqs = requiredItems.filter((r) => r.reqIndex !== 3)

    const placementZones = [
      { start: 0.15, end: 0.25 }, // Bliss
      { start: 0.45, end: 0.55 }, // Nova
      { start: 0.7, end: 0.8 }, // Symphony
    ]

    otherReqs.sort((a, b) => b.reqIndex - a.reqIndex)
    otherReqs.forEach((reqItem) => {
      const zone = placementZones[reqItem.reqIndex] || { start: 0.4, end: 0.6 }
      const zoneStart = Math.floor(allItems.length * zone.start)
      const zoneEnd = Math.floor(allItems.length * zone.end)
      const randomIndex = Math.floor(random() * (zoneEnd - zoneStart)) + zoneStart
      allItems.splice(randomIndex, 0, reqItem)
    })

    const displayItems = allItems.map((item, i) => {
      const btoName = String(item['bto-name'] || '').toLowerCase()
      const areaName = String(item['area-name'] || '')
      const descWord = String(item['descriptive-word'] || '')

      let isDescFirst = false
      const descIdx = btoName.indexOf(descWord.toLowerCase())
      const areaIdx = btoName.indexOf(areaName.toLowerCase())

      if (descIdx !== -1 && areaIdx !== -1) {
        isDescFirst = descIdx < areaIdx
      } else {
        isDescFirst = item['format-type'] === '2'
      }

      // Assign grid indices
      const rowIndex = Math.floor(i / itemsPerRow)
      const colIndex = i % itemsPerRow

      return {
        id: areaName + i,
        areaName,
        descWord,
        isDescFirst,
        rowIndex,
        colIndex,
        isRequired: item.isRequired,
        reqIndex: item.reqIndex,
        reqDescription: item.reqDescription,
        initialDelay: random() * 1000,
        initialDuration: 500 + random() * 1000,
        initialDirX: (random() - 0.5) * 120,
        initialDelayDesc: Math.floor(random() * 60),
        // layout properties
        x: 0,
        y: 0,
        width: 0,
        descWidth: 0,
        areaWidth: 0,
      }
    })

    // Special req3 handling
    if (req3) {
      req3.rowIndex = 25
      req3.colIndex = 7
      displayItems.push(req3)
    }

    cachedData = {
      items: displayItems,
      req3,
    }
    return cachedData
  })

  // --- 2. LAYOUT ENGINE ---
  const fontSize = $derived(innerWidth.current >= 768 ? 24 : 18)
  const lineGap = $derived(innerWidth.current >= 768 ? rowSpacingDesktop : rowSpacingMobile)
  const wordGap = $derived(innerWidth.current >= 768 ? 30 : 20)
  const highlightColor = 'rgba(207, 255, 94, 1)' // #cfff5e

  $effect(() => {
    // Explicitly register dependencies to trigger re-layout when Tweakpane values change
    lineGap
    wordGap
    fontSize
    if (isReady && ctx) {
      measureText()
    }
  })

  let gridCalculated = false
  let reqCoords = []

  const measureText = () => {
    if (!ctx || !displayData.items.length) return
    ctx.font = `${fontSize}px "Selane Twenty", "SelaneTwenty", serif`

    // Estimate total row widths to center them
    const rowWidths = Array(50).fill(0)
    const itemsByRow = Array(50)
      .fill(null)
      .map(() => [])

    displayData.items.forEach((w) => {
      if (w.reqIndex === 3) return // Handled separately

      const spaceWidth = ctx.measureText(' ').width
      w.descWidth = ctx.measureText(w.descWord).width
      w.areaWidth = ctx.measureText(w.areaName).width
      w.width = w.descWidth + spaceWidth + w.areaWidth

      if (w.rowIndex < 50) {
        rowWidths[w.rowIndex] += w.width + wordGap
        itemsByRow[w.rowIndex].push(w)
      }
    })

    // Assign X and Y coordinates
    const gridCenterY = 25 * lineGap
    itemsByRow.forEach((row, rIdx) => {
      const y = rIdx * lineGap - gridCenterY
      const isOffsetRow = rIdx % 2 !== 0

      let startX = -(rowWidths[rIdx] / 2)
      if (isOffsetRow) startX += 100 // Stagger offset

      let currentX = startX
      row.forEach((w) => {
        w.x = currentX + w.width / 2 // Center point
        w.y = y
        currentX += w.width + wordGap

        // Save target coords for camera
        if (w.isRequired && w.reqIndex >= 0 && w.reqIndex <= 2) {
          reqCoords[w.reqIndex] = { ex: w.x, ey: w.y }
        }
      })
    })

    // req3 is dead center
    if (displayData.req3) {
      reqCoords[3] = { ex: 0, ey: 0 }
    }

    gridCalculated = true
  }

  // --- 3. CAMERA LOGIC ---
  let maxZoom = $derived(innerWidth.current > 0 && innerWidth.current < 768 ? 1.6 : 2.5)

  const isReq3Active = $derived(index === 4 && offset <= 0.7)

  let stateProg = $derived.by(() => {
    let from = -1,
      to = -1,
      t = 0
    if (index === 0) {
      if (offset < 0.5) {
        from = -1
        to = -1
        t = 0
      } else {
        from = -1
        to = 0
        t = (offset - 0.5) * 2
      }
    } else if (index === 1) {
      if (offset < 0.5) {
        from = 0
        to = 0
        t = 1
      } else {
        from = 0
        to = 1
        t = (offset - 0.5) * 2
      }
    } else if (index === 2) {
      if (offset < 0.5) {
        from = 1
        to = 1
        t = 1
      } else {
        from = 1
        to = 2
        t = (offset - 0.5) * 2
      }
    } else if (index === 3) {
      if (offset < 0.5) {
        from = 2
        to = 2
        t = 1
      } else {
        from = 2
        to = -1
        t = (offset - 0.5) * 2
      }
    } else if (index === 4) {
      from = -1
      to = 3
      t = Math.min(1, offset * 2)
    } else if (index >= 5) {
      from = -1
      to = -1
      t = 0
    }
    t = Math.max(0, Math.min(1, t))
    return { from, to, t }
  })

  const getTarget = (n) => {
    if (n === -1) return { S: 1, x: 0, y: 0 }
    if (n === 3) return { S: 1, x: 0, y: 0 }
    if (!reqCoords[n]) return { S: 1, x: 0, y: 0 }
    return {
      S: maxZoom,
      x: -reqCoords[n].ex * maxZoom,
      y: -reqCoords[n].ey * maxZoom,
    }
  }

  let S = $derived.by(() => {
    const start = getTarget(stateProg.from)
    const end = getTarget(stateProg.to)
    return start.S + (end.S - start.S) * stateProg.t
  })

  let tx = $derived.by(() => {
    const start = getTarget(stateProg.from)
    const end = getTarget(stateProg.to)
    return start.x + (end.x - start.x) * stateProg.t
  })

  let ty = $derived.by(() => {
    const start = getTarget(stateProg.from)
    const end = getTarget(stateProg.to)
    return start.y + (end.y - start.y) * stateProg.t
  })

  let activeReqIndex = $derived.by(() => {
    if (index === 0) return -1
    if (stateProg.from === stateProg.to) return stateProg.to
    return stateProg.t > 0.5 ? stateProg.to : stateProg.from
  })

  let zoomProgress = $derived((S - 1) / (maxZoom - 1))

  // --- 4. RENDER LOOP ---
  // We use simple lerps for the push animations
  const pushOffsets = new Map() // word.id -> { current: 0, target: 0 }

  const resize = () => {
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    w = rect.width
    h = rect.height
    dpr = window.devicePixelRatio || 1
    canvas.width = w * dpr
    canvas.height = h * dpr
    ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)
    measureText()
  }

  const getWrappedLines = (ctx, text, maxWidth) => {
    const words = (text || '').split(' ')
    let line = ''
    let lines = []
    for (let n = 0; n < words.length; n++) {
      const testLine = line + (line ? ' ' : '') + words[n]
      const metrics = ctx.measureText(testLine)
      if (metrics.width > maxWidth && n > 0) {
        lines.push(line)
        line = words[n]
      } else {
        line = testLine
      }
    }
    if (line) lines.push(line)
    return lines.length ? lines : ['']
  }

  const updatePhysics = (w, globalDescVisible) => {
    const isActiveTarget = w.isRequired && w.reqIndex === activeReqIndex
    const isReq3Card = w.reqIndex === 3 && isReq3Active

    const dy = w.rowIndex - 25
    const dx = w.colIndex - 7
    const shouldPush = isReq3Active && w.reqIndex !== 3 && dy >= -4 && dy <= 3
    const pushTarget = shouldPush || isReq3Card ? 1 : 0

    let pushObj = pushOffsets.get(w.id)
    if (!pushObj) {
      pushObj = {
        current: 0,
        target: pushTarget,
        delay: 0,
        activeOpacity: isActiveTarget ? 1 : 0,
        animProgress: isActiveTarget ? 1 : 0,
        greenDelay: w.initialDelayDesc,
        greenOpacity: globalDescVisible ? 1 : 0,
      }
      pushOffsets.set(w.id, pushObj)
    }

    if (pushObj.target !== pushTarget) {
      pushObj.target = pushTarget
      if (pushTarget === 1) {
        pushObj.delay = Math.abs(dy) * 4 + Math.abs(dx) * 2
      } else {
        // Deterministic staggered delay to randomize words moving back
        pushObj.delay = (Math.abs(dy * 11 + dx * 17) % 25)
      }
    }

    if (pushObj.delay > 0) {
      pushObj.delay--
    } else {
      pushObj.current += (pushObj.target - pushObj.current) * 0.1
    }

    // Smooth transition for active state
    pushObj.activeOpacity += ((isActiveTarget ? 1 : 0) - pushObj.activeOpacity) * 0.08

    // Linear animation progress for sequential draw effects
    pushObj.animProgress = pushObj.animProgress || 0
    if (isActiveTarget || isReq3Card) {
      pushObj.animProgress = Math.min(1, pushObj.animProgress + animSpeed)
    } else {
      pushObj.animProgress = Math.max(0, pushObj.animProgress - (animSpeed * 2.66))
    }

    if (globalDescVisible) {
      if (pushObj.greenDelay > 0) {
        pushObj.greenDelay--
      } else {
        pushObj.greenOpacity += (1 - pushObj.greenOpacity) * 0.1
      }
    } else {
      pushObj.greenDelay = w.initialDelayDesc
      pushObj.greenOpacity += (0 - pushObj.greenOpacity) * 0.1
    }
  }

  const renderWord = (w, drawActiveOnly, globalDescVisible, zoomProgress, globalReq3Progress) => {
    const isReq3Card = w.reqIndex === 3 && isReq3Active
    let pushObj = pushOffsets.get(w.id)
    if (!pushObj) return

    // Skip if we are in the wrong pass
    const isDrawingActive = pushObj.activeOpacity > 0.01 || isReq3Card
    if (drawActiveOnly && !isDrawingActive) return
    if (!drawActiveOnly && isDrawingActive) return

    const dy = w.rowIndex - 25
    const dx = w.colIndex - 7
    const shouldPush = isReq3Active && w.reqIndex !== 3 && dy >= -4 && dy <= 3

    let pushX = 0
    if (dx !== 0 && pushObj.current > 0.01) {
      const basePush = emptyCardMaxWidth / 2 + 150
      pushX = (Math.sign(dx) * basePush + dx * 15) * pushObj.current
    }

    let initialOpacity = 1
    let initialOffsetX = 0
    if (introStartTime > 0) {
      const elapsed = performance.now() - introStartTime
      const animT = Math.max(0, Math.min(1, (elapsed - w.initialDelay) / w.initialDuration))
      if (animT < 1) {
        const easeT = 1 - Math.pow(1 - animT, 4)
        initialOffsetX = w.initialDirX * (1 - easeT)
        initialOpacity = easeT
      }
    }

    ctx.save()
    ctx.translate(w.x + pushX + initialOffsetX, w.y)

    // Opacity handling
    let opacity = 1
    if (w.reqIndex === 3) {
      opacity = pushObj.current
    } else if (shouldPush && dx === 0) {
      opacity = 1 - pushObj.current
    } else if (index >= 4) {
      opacity = 1 - (1 - emptyCardBgOpacity) * globalReq3Progress
    } else {
      const minOpacity = 0.15 + (1 - 0.15) * pushObj.activeOpacity
      opacity = 1 - zoomProgress * (1 - minOpacity)
    }
    opacity *= initialOpacity
    ctx.globalAlpha = opacity

    if (opacity > 0.01) {
      if (w.reqIndex === 3) {
        const descFontSize = emptyCardFontSize
        ctx.font = `${descFontSize}px ${emptyCardFontFamily}`
        ctx.textBaseline = 'middle'

        const descMaxWidth = emptyCardMaxWidth

        const descLines = getWrappedLines(ctx, w.reqDescription, descMaxWidth)
        const descLineHeight = descFontSize * emptyCardLineHeight

        const totalChars = descLines.reduce((acc, line) => acc + line.length, 0)
        let charsRemaining = enableLastCardTypingEffect
          ? Math.floor(totalChars * pushObj.animProgress)
          : totalChars

        let currentDescY = -((descLines.length - 1) * descLineHeight) / 2
        descLines.forEach((line) => {
          if (charsRemaining <= 0) return

          const lineToDraw = line.substring(0, charsRemaining)
          ctx.textAlign = 'left'
          const totalWidth = ctx.measureText(lineToDraw).width
          let currentX = -totalWidth / 2

          let localChars = charsRemaining
          const parts = line.split(/(public housing)/)

          for (const part of parts) {
            if (!part || localChars <= 0) continue
            const chunkToDraw = part.substring(0, localChars)

            ctx.fillStyle = part === 'public housing' ? highlightColor : '#ffffff'
            ctx.fillText(chunkToDraw, currentX, currentDescY)
            currentX += ctx.measureText(chunkToDraw).width

            localChars -= chunkToDraw.length
          }

          charsRemaining -= line.length
          currentDescY += descLineHeight
        })
      } else {
        // Normal word
        // 1. Draw Active Target Box
        if (pushObj.activeOpacity > 0.01 && w.isRequired && w.reqIndex >= 0 && w.reqIndex <= 2) {
          const paddingX = 15
          const paddingY = 15 / maxZoom
          const descFontSize = activeDescFontSize / maxZoom

          ctx.font = `${descFontSize}px ${activeDescFontFamily}`
          const maxBoxWidthCanvas = (innerWidth.current - 30) / maxZoom
          const descMaxWidth = Math.min(380 / maxZoom, maxBoxWidthCanvas - paddingX * 2)

          const descLines = getWrappedLines(ctx, w.reqDescription, descMaxWidth)
          const descLineHeight = descFontSize * activeDescLineHeight
          const totalDescHeight = descLines.length * descLineHeight

          const actualDescWidth = Math.max(...descLines.map((l) => ctx.measureText(l).width))
          const contentWidthFinal = Math.max(w.width, actualDescWidth)

          const rWidth = contentWidthFinal + paddingX * 2

          const gap = activeDescGap / maxZoom
          const boxY = -fontSize / 2 - paddingY
          const boxBottom = fontSize / 2 + gap + totalDescHeight + paddingY
          const rHeight = boxBottom - boxY

          // Split animProgress into 2 phases: Border (0.0 to 0.5) and Text (0.5 to 1.0)
          const borderProgress = enableBorderAnimation ? Math.min(1, pushObj.animProgress * 2) : 1
          const textProgress = Math.max(0, (pushObj.animProgress - 0.5) * 2)

          // Apply transition opacity to the box
          ctx.globalAlpha = pushObj.activeOpacity * initialOpacity

          ctx.strokeStyle = highlightColor
          ctx.lineWidth = 1 / maxZoom

          ctx.fillStyle = activeBoxBgColor

          ctx.beginPath()
          const boxX = -contentWidthFinal / 2 - paddingX
          if (ctx.roundRect) {
            ctx.roundRect(boxX, boxY, rWidth, rHeight, 1 / maxZoom)
          } else {
            ctx.rect(boxX, boxY, rWidth, rHeight)
          }
          ctx.fill()

          // Draw border with stroke animation
          const perimeter = (rWidth + rHeight) * 2 // rough approximation
          ctx.setLineDash([perimeter, perimeter])
          ctx.lineDashOffset = perimeter * (1 - borderProgress)
          ctx.stroke()
          ctx.setLineDash([]) // Reset!

          // Draw long desc with typewriter effect
          ctx.fillStyle = highlightColor
          ctx.textAlign = 'center'
          ctx.textBaseline = 'top'
          let currentDescY = fontSize / 2 + gap

          const totalChars = descLines.reduce((acc, line) => acc + line.length, 0)
          let charsRemaining = enableTypingEffect
            ? Math.floor(totalChars * textProgress)
            : totalChars

          descLines.forEach((line) => {
            if (charsRemaining <= 0) return
            const textToDraw = line.substring(0, charsRemaining)
            ctx.fillText(textToDraw, 0, currentDescY)
            charsRemaining -= line.length
            currentDescY += descLineHeight
          })

          // Reset globalAlpha to word opacity
          ctx.globalAlpha = opacity
        }

        ctx.font = `${fontSize}px "Selane Twenty", "SelaneTwenty", serif`
        ctx.textBaseline = 'middle'

        const spaceWidth = ctx.measureText(' ').width
        const drawDescAlpha = Math.max(pushObj.greenOpacity, pushObj.activeOpacity)

        // Small slide offset when turning green globally
        let slideOffset = 0
        if (pushObj.greenOpacity < 0.99 && pushObj.activeOpacity < 0.01) {
          slideOffset = w.initialDirX * 0.3 * (1 - pushObj.greenOpacity)
        }

        let curX = -w.width / 2

        if (w.isDescFirst) {
          ctx.fillStyle = `rgba(207, 255, 94, ${drawDescAlpha})`
          ctx.textAlign = 'left'
          ctx.fillText(w.descWord, curX + slideOffset, 0)
          curX += w.descWidth + spaceWidth

          ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + 0.5 * pushObj.activeOpacity})`
          ctx.fillText(w.areaName, curX, 0)
        } else {
          ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + 0.5 * pushObj.activeOpacity})`
          ctx.textAlign = 'left'
          ctx.fillText(w.areaName, curX, 0)
          curX += w.areaWidth + spaceWidth

          ctx.fillStyle = `rgba(207, 255, 94, ${drawDescAlpha})`
          ctx.fillText(w.descWord, curX + slideOffset, 0)
        }
      }
    }

    ctx.restore()
  }

  const render = () => {
    if (!ctx || !gridCalculated) {
      frameId = requestAnimationFrame(render)
      return
    }

    ctx.clearRect(0, 0, w, h)

    ctx.save()
    ctx.translate(w / 2, h / 2) // Center of screen
    ctx.translate(tx, ty) // Camera pan
    ctx.scale(S, S) // Camera zoom

    const offsetThreshold = 0.1
    const globalDescVisible = (index === 0 && offset >= offsetThreshold) || index >= 1

    const currentMaxZoom = maxZoom
    const zoomProgress = currentMaxZoom > 1 ? (S - 1) / (currentMaxZoom - 1) : 0

    let globalReq3Progress = 0
    if (index >= 4 && displayData.req3) {
      const req3PushObj = pushOffsets.get(displayData.req3.id)
      if (req3PushObj) {
        globalReq3Progress = req3PushObj.animProgress
      }
    }

    // Physics pass
    displayData.items.forEach((w) => updatePhysics(w, globalDescVisible))

    // Pass 1: Draw non-active words
    displayData.items.forEach((w) =>
      renderWord(w, false, globalDescVisible, zoomProgress, globalReq3Progress),
    )

    // Pass 2: Draw active words on top
    displayData.items.forEach((w) =>
      renderWord(w, true, globalDescVisible, zoomProgress, globalReq3Progress),
    )

    ctx.restore()
    frameId = requestAnimationFrame(render)
  }

  onMount(() => {
    const rootStyles = getComputedStyle(document.body)
    const sans = rootStyles.getPropertyValue('--font-sans-serif').trim()
    if (sans) {
      activeDescFontFamily = sans
    }

    introStartTime = performance.now()
    document.fonts.ready.then(() => {
      resize()
      isReady = true
    })
    window.addEventListener('resize', resize)
    frameId = requestAnimationFrame(render)
  })

  onDestroy(() => {
    if (browser) window.removeEventListener('resize', resize)
    if (frameId) cancelAnimationFrame(frameId)
  })
</script>

<div class="wrap">
  <canvas bind:this={canvas} class="brick-canvas" class:ready={isReady}></canvas>
</div>

<style lang="scss">
  .wrap {
    width: 100%;
    height: 100lvh;
    background-color: var(--body-background-color);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
  }

  .brick-canvas {
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 1.2s ease-in-out;
  }

  .brick-canvas.ready {
    opacity: 1;
  }
</style>
